import { Schema, SchemaTransformation, Struct } from 'effect';
import { matchSpaceTime, matchSpaceTimeAndSlotKey, parseSpaceTime } from '../spacetime/spacetime.js';
import { velprAccessCodeSpaceTimeConfig } from '../spacetime/spacetimeConfig.js';
import { accessPointById } from '../sitedata/accessPoint.js';
import { sortAndReindex } from '../utils/utils.js';
import { slotToDateTime } from '../slotKey.js';
import { isWithinValidity, validityEnd } from '../validity.js';
var CodeUsageEnum;
(function (CodeUsageEnum) {
    CodeUsageEnum["eventDiscount"] = "eventDiscount";
    CodeUsageEnum["eventSignup"] = "eventSignup";
    CodeUsageEnum["hourBooking"] = "hourBooking";
    CodeUsageEnum["hourDiscount"] = "hourDiscount";
    CodeUsageEnum["seasonTicket"] = "seasonTicket";
})(CodeUsageEnum || (CodeUsageEnum = {}));
const mutableStruct = (fields) => Schema.Struct(fields).mapFields(Struct.map(Schema.mutableKey));
const AccessCodeSchema = mutableStruct({
    id: Schema.Number, // This is really the order in which it is evaluated
    code: Schema.String,
    spaceTime: Schema.String, // When and for what the code is valid
    validFrom: Schema.String, // Date when this entry takes effect
    validTo: Schema.String, // Date when this entry no longer apply
    freeText: Schema.String,
    // If set, code is scoped to spaces referencing this access point
    accessPointId: Schema.optional(Schema.String)
});
const CodeUsageSchema = mutableStruct({
    eventDiscount: Schema.Boolean,
    eventSignup: Schema.Boolean,
    hourBooking: Schema.Boolean,
    hourDiscount: Schema.Boolean,
    seasonTicket: Schema.Boolean
});
const DiscountSchema = mutableStruct({
    type: Schema.Literals(['percentage', 'fixed']),
    value: Schema.Number
});
const GeneralDiscountSchema = mutableStruct({
    /**
     * A blanket discount on every rental slot the code is valid for. Applied by
     * live.pricer.
     *
     * There is deliberately no event counterpart. One existed, was authorable,
     * and was read by nothing — so any site that configured it had a setting
     * that silently did nothing. Rather than switch it on (which would have
     * repriced live events without warning) it was removed. An event discount
     * is configured per sub-event instead, on `EventSub.price.discount`, where
     * a `club:` eligibility pattern does the same job explicitly.
     * See docs/plans/groups.md.
     */
    rentalDiscount: Schema.optional(DiscountSchema)
});
const ClubCodeSchema = mutableStruct({
    // Stable and unique, 'club-000000000007'; not the order (that is the array
    // order). Older documents hold a number, see toClubCodeId.
    id: Schema.String,
    name: Schema.String, // Display name
    slug: Schema.String, // Used for logic/matching (manually entered)
    code: Schema.String,
    usage: CodeUsageSchema,
    lockedUsers: Schema.String, // list of emails that are allowed to use this code - maybe in the future group id too
    lockedItems: Schema.String, // ids of items, events that this code can be applied to - if empty then all
    spaceTime: Schema.String, // This is space time for what and when the code is unlocking stuff (applies only to hours)
    validTo: Schema.String, // This is a datetime where the club code expires - a specific date
    validFrom: Schema.String, // This is so you can add a club code that will be enabled in the future
    freeText: Schema.String, // If you want to attach a note or something to the code
    generalDiscount: Schema.optional(GeneralDiscountSchema)
});
const CodesDocType = mutableStruct({
    accessCodes: Schema.mutable(Schema.Array(AccessCodeSchema)),
    clubCodes: Schema.mutable(Schema.Array(ClubCodeSchema)),
    /**
     * Instruksjoner (eksternt) per access point id. Kept here, admin-readable,
     * instead of on the world-readable site document (D69, F-RULES-08); served
     * to ticket holders by GET /code/:site/accessinfo/:itemKey. Optional: an
     * access point missing here falls back to the legacy
     * `AccessPoint.external.instructions` until the Adgang page is next saved.
     */
    accessPointInstructions: Schema.optional(Schema.Record(Schema.String, Schema.String)),
    version: Schema.Number
});
// What may be in Firestore: every field optional, plus the shapes the
// migrations in toCodesDoc read (numeric club ids, `items`).
const stored = (schema) => Schema.optional(schema);
const StoredAccessCode = Schema.Struct({
    id: stored(Schema.Number),
    code: stored(Schema.String),
    spaceTime: stored(Schema.String),
    // Deprecated: the items part of the spaceTime, from before it had one.
    items: stored(Schema.String),
    validFrom: stored(Schema.String),
    validTo: stored(Schema.String),
    freeText: stored(Schema.String),
    accessPointId: stored(Schema.String)
});
const StoredClubCode = Schema.Struct({
    id: stored(Schema.Union([Schema.String, Schema.Number])),
    name: stored(Schema.String),
    slug: stored(Schema.String),
    code: stored(Schema.String),
    usage: stored(Schema.Struct({
        eventDiscount: stored(Schema.Boolean),
        eventSignup: stored(Schema.Boolean),
        hourBooking: stored(Schema.Boolean),
        hourDiscount: stored(Schema.Boolean),
        seasonTicket: stored(Schema.Boolean)
    })),
    lockedUsers: stored(Schema.String),
    lockedItems: stored(Schema.String),
    spaceTime: stored(Schema.String),
    validTo: stored(Schema.String),
    validFrom: stored(Schema.String),
    freeText: stored(Schema.String),
    generalDiscount: stored(Schema.Struct({ rentalDiscount: stored(DiscountSchema) }))
});
const StoredCodesFields = Schema.Struct({
    accessCodes: stored(Schema.Array(StoredAccessCode)),
    clubCodes: stored(Schema.Array(StoredClubCode)),
    accessPointInstructions: stored(Schema.Record(Schema.String, Schema.String)),
    version: stored(Schema.Number)
});
/**
 * `sites/{site}/config/codes` as stored. The stored side checks the type of
 * every field; the migrations and defaults are toCodesDoc's, run on the
 * checked document, so the two never drift while both exist. When the
 * converter goes, its body moves into this transform.
 */
const CodesDocSchema = StoredCodesFields.pipe(Schema.decodeTo(CodesDocType, SchemaTransformation.transform({
    decode: (doc) => toCodesDoc(doc),
    encode: (doc) => doc
})));
const emptyCodesDoc = () => {
    return {
        accessCodes: [],
        clubCodes: [],
        version: 1
    };
};
/**
 * A club code id in its stable form, 'club-000000000002'.
 *
 * Club codes once had numeric ids that doubled as their order, and until
 * 2026-10 every read replaced the id with the code's position in the list.
 * So whatever number a ticket recorded ('2') or a cart copied was a
 * position, and that is what a number becomes here. toCodesDoc passes the
 * position, not the stored number, for the same reason: a document not saved
 * since may still hold its original ids.
 */
const toClubCodeId = (raw) => {
    if (typeof raw === 'number')
        return `club-${String(raw).padStart(12, '0')}`;
    if (typeof raw !== 'string')
        return '';
    return /^\d+$/.test(raw) ? toClubCodeId(Number(raw)) : raw;
};
/** A stored club code's id: kept if it is a string, else from its position. */
const storedClubCodeId = (raw, position) => typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(raw))
    ? toClubCodeId(position)
    : toClubCodeId(raw);
const convertOldSpaceTime = (raw) => {
    return raw.spaceTime ?? (raw.items || '') + '@' + raw.validFrom;
};
const converOldValidFrom = (raw) => {
    if ('@' + raw.validFrom === convertOldSpaceTime(raw))
        return '';
    return raw.validFrom ?? '';
};
const toCodesDoc = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return emptyCodesDoc();
    }
    const result = emptyCodesDoc();
    for (const accessCode of raw.accessCodes || []) {
        const resultAC = {
            id: accessCode.id ?? '',
            validTo: accessCode.validTo ?? '',
            code: accessCode.code ?? '',
            validFrom: converOldValidFrom(accessCode),
            spaceTime: convertOldSpaceTime(accessCode),
            freeText: accessCode.freeText ?? '',
            accessPointId: typeof accessCode.accessPointId === 'string' &&
                accessCode.accessPointId !== ''
                ? accessCode.accessPointId
                : undefined
        };
        result.accessCodes.push(resultAC);
    }
    sortAndReindex(result.accessCodes, 'id');
    if (raw.accessPointInstructions &&
        typeof raw.accessPointInstructions === 'object') {
        const instructions = {};
        for (const [id, text] of Object.entries(raw.accessPointInstructions)) {
            if (typeof text === 'string')
                instructions[id] = text;
        }
        result.accessPointInstructions = instructions;
    }
    for (const [position, code] of (raw.clubCodes || []).entries()) {
        const resultCC = {
            id: storedClubCodeId(code.id, position),
            name: code.name ?? '',
            // Generate slug from name if not present
            slug: code.slug ||
                (code.name ? code.name.toLowerCase().replace(/\s+/g, '-') : ''),
            validTo: code.validTo ?? '',
            code: code.code ?? '',
            lockedItems: code.lockedItems ?? '',
            lockedUsers: code.lockedUsers ?? '',
            validFrom: code.validFrom ?? '',
            spaceTime: code.spaceTime ?? '',
            freeText: code.freeText ?? '',
            usage: {
                eventDiscount: code.usage?.eventDiscount ?? false,
                eventSignup: code.usage?.eventSignup ?? false,
                hourBooking: code.usage?.hourBooking ?? true,
                hourDiscount: code.usage?.hourDiscount ?? false,
                seasonTicket: code.usage?.seasonTicket ?? false
            },
            generalDiscount: code.generalDiscount
                ? { rentalDiscount: code.generalDiscount.rentalDiscount }
                : undefined
        };
        result.clubCodes.push(resultCC);
    }
    return result;
};
const CodesStored = {
    name: 'CodesDoc',
    schema: CodesDocSchema,
    legacy: toCodesDoc,
    authority: 'legacy'
};
/**
 * The slots a club code applies to - for unlocking, discount and season
 * ticket alike. An empty spaceTime means the whole site.
 */
const clubCodeSpaceTime = (code, site) => parseSpaceTime({ str: code.spaceTime || '@', site });
/**
 * Does the club code's Gyldig fra/til cover this slot? The window says which
 * slots the code covers, so it is checked against the slot's start in the
 * site's zone, with the end date included (ADR 0014). Used by the unlock in
 * live.v2 and by the server, so they always agree.
 */
const clubCodeCoversSlotTime = (code, slotKey, site) => {
    const timezone = site.settings?.timezone;
    return isWithinValidity(slotToDateTime(slotKey, timezone), code, timezone);
};
/**
 * Has the club code's window ended, so it can no longer cover any slot? A code
 * that starts later is still good - it covers the slots its window says. Used
 * by the lookup endpoint (D50).
 */
const clubCodeHasEnded = (code, now, timezone) => {
    const end = validityEnd(code.validTo, timezone);
    return !!end && now > end;
};
const checkClubCode = (item, code, site) => {
    if (!clubCodeCoversSlotTime(code, item.slotKey, site))
        return false;
    const spaceTime = clubCodeSpaceTime(code, site);
    const match = matchSpaceTimeAndSlotKey(item.slotKey, spaceTime);
    return !!match;
};
const currentAccessCode = (item, codes, dt, site) => {
    const space = site.siteItems.find((s) => s.id === item);
    const spaceAccessPointId = space?.accessPointId;
    for (const code of codes) {
        // dt is the slot's time: the code's window says which slots it covers
        if (!isWithinValidity(dt, code, site.settings?.timezone))
            continue;
        if (code.accessPointId) {
            // AP-linked code: only matches if the slot's space references the same AP.
            if (spaceAccessPointId !== code.accessPointId)
                continue;
            // For velpr APs, the code's spaceTime is just a filter on top of the
            // AP (which already constrains spaces and is system-managed). Parse it
            // against a 24-hour-day default so a fragment like "mon" covers the
            // whole Monday, not just the site's opening hours.
            const ap = accessPointById(site, code.accessPointId);
            const siteForParse = ap?.type === 'velpr'
                ? { ...site, spaceTimes: velprAccessCodeSpaceTimeConfig() }
                : site;
            const spaceTime = parseSpaceTime({
                str: code.spaceTime || '@',
                site: siteForParse
            });
            // matchSpaceTime treats an empty items list as a wildcard, so an
            // AP-linked spaceTime expression typically only encodes day/time.
            if (matchSpaceTime(item, dt, spaceTime))
                return code.code;
        }
        else {
            // Legacy code: do not steal slots from spaces that have moved to an AP-velpr setup.
            if (spaceAccessPointId)
                continue;
            const spaceTime = parseSpaceTime({ str: code.spaceTime, site });
            if (matchSpaceTime(item, dt, spaceTime))
                return code.code;
        }
    }
    return '';
};
export { CodesDocSchema, toClubCodeId, CodesStored, emptyCodesDoc, toCodesDoc, currentAccessCode, checkClubCode, clubCodeCoversSlotTime, clubCodeHasEnded, clubCodeSpaceTime, CodeUsageEnum };
//# sourceMappingURL=codes.js.map